import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  X,
  MessageCircle,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react'
import { useCan } from '@/features/auth'
import { Pagination } from '@/shared/components/ui/pagination'
import {
  fetchAdminCommercials,
  deleteCommercial,
  refreshCommercial,
  type AdminCommercial,
  type AdminCommercialsResult,
} from '@/features/commercials/commercials'
import { CommercialEditor } from './CommercialEditor'

const PAGE_SIZE = 10

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'
}

export function CommercialsAdminPage() {
  const canEdit = useCan('landing:edit')
  // null = lista; 'new' = alta; objeto = edición de ese comercial.
  const [editing, setEditing] = useState<AdminCommercial | 'new' | null>(null)
  const [data, setData] = useState<AdminCommercialsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [refreshingId, setRefreshingId] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    setPage(1)
  }, [debounced])

  useEffect(() => {
    if (!canEdit || editing) return
    let active = true
    setLoading(true)
    fetchAdminCommercials({ page, pageSize: PAGE_SIZE, q: debounced })
      .then((d) => {
        if (!active) return
        if (d.items.length === 0 && d.total > 0 && page > 1) setPage((p) => Math.max(1, p - 1))
        else setData(d)
      })
      .catch(() => active && toast.error('No se pudieron cargar los comerciales'))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [canEdit, editing, page, debounced, reloadKey])

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">Esta sección es exclusiva del administrador del portal.</p>
      </div>
    )
  }

  const reload = () => setReloadKey((k) => k + 1)

  const remove = async (id: string) => {
    try {
      await deleteCommercial(id)
      reload()
    } catch {
      toast.error('No se pudo eliminar el comercial')
    }
  }

  const refresh = async (id: string) => {
    setRefreshingId(id)
    try {
      await refreshCommercial(id)
      reload()
      toast.success('Perfil actualizado desde Identidad')
    } catch {
      toast.error('Identidad no respondió; se conserva el último dato')
    } finally {
      setRefreshingId(null)
    }
  }

  // -------- EDITOR --------
  if (editing) {
    return (
      <CommercialEditor
        editing={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reload()
        }}
      />
    )
  }

  // -------- LISTADO --------
  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-brand">Administración del portal</span>
          <h1 className="mt-1 font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">Comerciales</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Comerciales certificados del modal "Contactar con un comercial". Cada uno es un usuario de la plataforma:
            su nombre, foto y teléfono se toman de Identidad y se mantienen al día solos.
          </p>
        </div>
        <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
          <Plus className="size-4" /> Nuevo comercial
        </button>
      </header>

      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o WhatsApp…"
          className="w-full rounded-lg border border-line bg-white py-2 pl-9 pr-9 text-sm text-ink outline-none focus:border-brand"
        />
        {query && (
          <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-ink-subtle hover:text-ink">
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-card border border-line bg-panel py-16 text-ink-muted">
          <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-card border border-dashed border-line bg-panel p-10 text-center">
          <p className="text-sm text-ink-muted">
            {total === 0 && !query.trim()
              ? 'Aún no hay comerciales. Añade el primero buscándolo en Identidad con "Nuevo comercial".'
              : 'No hay comerciales que coincidan con la búsqueda.'}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((c) => (
              <li key={c.id} className="flex items-center gap-4 rounded-card border border-line bg-panel p-3">
                {c.photo ? (
                  <img src={c.photo} alt="" className="size-12 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand">{initials(c.name)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold text-ink">{c.name || 'Sin nombre'}</h3>
                    {!c.active && <span className="rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-subtle">Inactivo</span>}
                    {c.unresolved && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700" title="Identidad aún no ha resuelto este perfil">
                        <AlertTriangle className="size-2.5" /> Sin resolver
                      </span>
                    )}
                  </div>
                  <p className="flex items-center gap-1 truncate text-xs text-ink-muted">
                    <MessageCircle className="size-3" /> {c.whatsappEffective || '—'}
                    {c.whatsappOverride && <span className="text-ink-subtle">(alternativo)</span>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => void refresh(c.id)}
                    disabled={refreshingId === c.id}
                    className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink disabled:opacity-50"
                    title="Refrescar desde Identidad"
                  >
                    <RefreshCw className={`size-4 ${refreshingId === c.id ? 'animate-spin' : ''}`} />
                  </button>
                  <button onClick={() => setEditing(c)} className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink" title="Editar">
                    <Pencil className="size-4" />
                  </button>
                  <button onClick={() => void remove(c.id)} className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-soft hover:text-danger" title="Eliminar">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <Pagination page={page} pageSize={PAGE_SIZE} totalItems={total} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
